//
// Copyright (c) 2011-2020 by Curalate, Inc.
//
import moment from 'moment-timezone';

import '@legacy-components/common/universal-image-actions/universal-image-actions-api-service';
import { IWindowService } from 'angular';
import { GaApi } from 'app/initialization/ga/ga-api';
import _ from 'underscore';

import '@instagram-account/instagram-account-promoted-posts/promoted-posts-eligibility-service';
import PromotedPostsEligibilityService from '@instagram-account/instagram-account-promoted-posts/promoted-posts-eligibility-service';
import { fillInMissingDates } from '@instagram-account/redux/engagement/instagramAccountEngagementUtils';
import { EcommerceProvider } from '@models/MetricsGatewayApiModels';
import { IUnisonDateRange } from '@models/UnisonTypes';
import MetricsGatewayApiService from '@services/metrics-gateway-api-service';
import { SessionVarsService } from '@services/session-vars-service';
import { IStateService } from 'angular-ui-router';
import {
  PROMOTED_POSTS_METRICS_ACTIONS,
  PromotedPostsMetricsStatuses,
} from 'app/promoted-posts-metrics/promoted-posts-metrics-constants/promoted-posts-metrics-constants';
import PromotedPostsMetricsGatewayApiService from 'app/promoted-posts-metrics/promoted-posts-metrics-gateway-api-service';
import promotedPostsMetricsModule from 'app/promoted-posts-metrics/promoted-posts-metrics-module';
import {
  IDailyPromotedPostSummary,
  IPromotedPostsMetricsOnboardingResponse,
  IPromotedPostsStatusUpdateResponse,
} from '../models/promoted-posts-metrics-request-types';
import '../promoted-posts-metrics-gateway-api-service';
import {
  AggregatePromotedPostsValueLabels,
  AggregatePromotedPostsValues,
} from '../promoted-posts-metrics-stats/promoted-posts-metrics-stats-values';

const RECENT_PROMOTIONS_LIMIT = 6;

const GOOGLE_TRACKING_PARAM_ID = 'google';

function receiveOnboardingStepsStateSucceeded(
  promotionMetricsOnboardingResponse: IPromotedPostsMetricsOnboardingResponse,
) {
  return {
    payload: promotionMetricsOnboardingResponse,
    type:
      PROMOTED_POSTS_METRICS_ACTIONS.RECEIVE_ONBOARDING_STEPS_STATE_SUCCEEDED,
  };
}

function receiveOnboardingStepsStateFailed(error) {
  return {
    payload: error,
    type: PROMOTED_POSTS_METRICS_ACTIONS.RECEIVE_ONBOARDING_STEPS_STATE_FAILED,
  };
}

function fetchOnboardingStepsStateAction() {
  return {
    type: PROMOTED_POSTS_METRICS_ACTIONS.FETCH_ONBOARDING_STEPS_STATE,
  };
}

function fetchGAStateAction() {
  return {
    type: PROMOTED_POSTS_METRICS_ACTIONS.FETCH_GA_STATE,
  };
}

function receiveGAStateSucceeded(data) {
  return {
    payload: {
      isGaConnected: data,
    },
    type: PROMOTED_POSTS_METRICS_ACTIONS.RECEIVE_GA_STATE,
  };
}

function requestPostSummaryStatsAction() {
  return {
    type: PROMOTED_POSTS_METRICS_ACTIONS.REQUEST_SUMMARY_STATS,
  };
}

function receivePostAggregateStats(data) {
  return {
    payload: {
      aggregateStats: data,
    },
    type: PROMOTED_POSTS_METRICS_ACTIONS.RECEIVE_AGGREGATE_STATS,
  };
}

function receivePostTrendStats(data) {
  return {
    payload: {
      trendStats: data,
    },
    type: PROMOTED_POSTS_METRICS_ACTIONS.RECEIVE_TREND_STATS,
  };
}

function changeDateRange(dateRange: IUnisonDateRange) {
  return {
    payload: {
      newDateRange: dateRange,
    },
    type: PROMOTED_POSTS_METRICS_ACTIONS.CHANGE_DATE_RANGE,
  };
}

function changeTrendsSelectedValue(value) {
  return {
    payload: {
      trendSelectedValue: value,
    },
    type: PROMOTED_POSTS_METRICS_ACTIONS.CHANGE_TRENDS_SELECTED_VALUE,
  };
}

function fetchPromotedPostsAction() {
  return {
    type: PROMOTED_POSTS_METRICS_ACTIONS.FETCH_PROMOTED_POSTS,
  };
}

function receivePromotedPostsAction(data) {
  return {
    payload: {
      totalPostCount: data.total,
      promotedPostsStats: data.posts,
    },
    type: PROMOTED_POSTS_METRICS_ACTIONS.RECEIVE_PROMOTED_POSTS_SUCCEEDED,
  };
}

function receivePromotedPostsFailed(error) {
  return {
    payload: error,
    type: PROMOTED_POSTS_METRICS_ACTIONS.RECEIVE_PROMOTED_POSTS_FAILED,
  };
}

function fetchRecentPromotedPostsAction() {
  return {
    type: PROMOTED_POSTS_METRICS_ACTIONS.FETCH_RECENT_PROMOTED_POSTS,
  };
}

function receiveRecentPromotedPostsAction(data, hasPromotedPosts: boolean) {
  return {
    payload: {
      recentPromotedPostsStats: data.posts,
      hasPromotedPosts,
    },
    type:
      PROMOTED_POSTS_METRICS_ACTIONS.RECEIVE_RECENT_PROMOTED_POSTS_SUCCEEDED,
  };
}

function receiveRecentPromotedPostsFailed(error) {
  return {
    payload: error,
    type: PROMOTED_POSTS_METRICS_ACTIONS.RECEIVE_RECENT_PROMOTED_POSTS_FAILED,
  };
}

function changePromotedPostsTableParams(limit, offset, sortKey, sortDir) {
  return {
    payload: {
      limit,
      offset,
      sortKey,
      sortDir,
    },
    type: PROMOTED_POSTS_METRICS_ACTIONS.CHANGE_PROMOTED_POSTS_TABLE_PARAMS,
  };
}

function updatePromotionStatusAction(
  campaignId,
  promotionStatusResponse: IPromotedPostsStatusUpdateResponse,
) {
  return {
    payload: {
      campaignId,
      promotionStatusResponse,
    },
    type: PROMOTED_POSTS_METRICS_ACTIONS.UPDATE_PROMOTION_STATUS,
  };
}

function pausePromotionAction(campaignId) {
  return {
    payload: {
      campaignId,
    },
    type: PROMOTED_POSTS_METRICS_ACTIONS.PAUSE_PROMOTION,
  };
}

function resumePromotionAction(campaignId) {
  return {
    payload: {
      campaignId,
    },
    type: PROMOTED_POSTS_METRICS_ACTIONS.RESUME_PROMOTION,
  };
}

function deletePromotionAction(campaignId) {
  return {
    payload: {
      campaignId,
    },
    type: PROMOTED_POSTS_METRICS_ACTIONS.DELETE_PROMOTION,
  };
}

function updateInsightsForPostAction(postId: string) {
  return {
    payload: {
      postId,
    },
    type: PROMOTED_POSTS_METRICS_ACTIONS.UPDATE_INSIGHTS_FOR_POST,
  };
}

function updateRunningPromotionPostIds(runningPromotionPostIds: Set<string>) {
  return {
    payload: {
      runningPromotionPostIds,
    },
    type: PROMOTED_POSTS_METRICS_ACTIONS.UPDATE_RUNNING_PROMOTION_POST_IDS,
  };
}
function updatePostPromotionStates(promotionStates) {
  return {
    payload: {
      promotionStates,
    },
    type: PROMOTED_POSTS_METRICS_ACTIONS.UPDATE_POST_PROMOTION_STATES,
  };
}

function requestCurrentlyRunningAdsAction() {
  return {
    type: PROMOTED_POSTS_METRICS_ACTIONS.REQUEST_CURRENTLY_RUNNING,
  };
}

function updateIsGaEcommerce(isGaEcommerceConnected) {
  return {
    payload: {
      isGaEcommerceConnected,
    },
    type: PROMOTED_POSTS_METRICS_ACTIONS.UPDATE_GA_ECOMMERCE_CONNECTED,
  };
}

export type IPromotedPostsMetricsActions = ReturnType<
  typeof PromotedPostsMetricsActions
>;

function PromotedPostsMetricsActions(
  $window: IWindowService,
  $state: IStateService,
  metricsGatewayApiService: MetricsGatewayApiService,
  PromotedPostsMetricsGatewayApi: PromotedPostsMetricsGatewayApiService,
  PromotedPostsEligibility: PromotedPostsEligibilityService,
  sessionVarsService: SessionVarsService,
  trackingService: GaApi,
  UniversalImageActionsApiService,
) {
  function fetchPromotedPosts(
    startDate,
    endDate,
    limit,
    offset,
    sortKey,
    sortDirection,
  ) {
    return (dispatch) => {
      dispatch(fetchPromotedPostsAction());

      const userTimezone = sessionVarsService.getTimeZoneId();
      const startDateString = moment(startDate)
        .tz(userTimezone)
        .format('YYYY-MM-DD');
      const endDateString = moment(endDate)
        .tz(userTimezone)
        .format('YYYY-MM-DD');

      PromotedPostsMetricsGatewayApi.getPromotedPostsMetricsForRange(
        startDateString,
        endDateString,
        userTimezone,
        limit,
        offset,
        sortKey,
        sortDirection,
      )
        .then((response) => {
          dispatch(receivePromotedPostsAction(response));
        })
        .catch((error) => {
          dispatch(receivePromotedPostsFailed(error));
        });
    };
  }

  function fetchRecentPromotedPosts(limit: number = RECENT_PROMOTIONS_LIMIT) {
    return async (dispatch) => {
      dispatch(fetchRecentPromotedPostsAction());

      try {
        const response = await PromotedPostsMetricsGatewayApi.getRecentPromotionsForClientAsync(
          limit,
        );
        const postsList = response?.posts ?? [];
        const hasPromotedPosts = postsList.length > 0;
        dispatch(receiveRecentPromotedPostsAction(response, hasPromotedPosts));
      } catch (e) {
        dispatch(receiveRecentPromotedPostsFailed(e));
      }
    };
  }

  function fetchOnboardingStepsState() {
    return (dispatch) => {
      dispatch(fetchOnboardingStepsStateAction);
      PromotedPostsMetricsGatewayApi.fetchOnboardingState()
        .then((response) => {
          const stepsStateValues = _(response).values();
          const isOnboarded: boolean = _(stepsStateValues).reduce(
            (memo, step: any) => memo && step,
            true,
          );

          if (isOnboarded) {
            // We need to verify that the client has any promotions before we let them view the metrics page
            dispatch(fetchRecentPromotedPosts(RECENT_PROMOTIONS_LIMIT));
          }

          dispatch(
            receiveOnboardingStepsStateSucceeded({
              isOnboarded,
              stepsState: {
                ...response,
              },
            }),
          );
        })
        .catch((error) => {
          dispatch(receiveOnboardingStepsStateFailed(error));
        });
    };
  }

  function fetchGAEcommerceState() {
    return (dispatch) => {
      metricsGatewayApiService.getMetadata().then((response) => {
        const isGoogleAnalytics =
          response.ecommerceProvider === EcommerceProvider.GoogleAnalytics;
        const isDemo = response.ecommerceProvider === EcommerceProvider.Demo;
        const hasTracking = response.ecommerceTracking;
        dispatch(
          updateIsGaEcommerce((hasTracking && isGoogleAnalytics) || isDemo),
        );
      });
    };
  }

  function fetchGAState() {
    return (dispatch) => {
      dispatch(fetchGAStateAction);
      UniversalImageActionsApiService.getCurrentTrackingParams().then(
        (response) => {
          const googleParamGroups: any = _(
            response.trackingParams.paramGroups,
          ).find((trackingParamGroup) => {
            return (
              trackingParamGroup.id === GOOGLE_TRACKING_PARAM_ID &&
              trackingParamGroup.inUse
            );
          });

          const googleParams = googleParamGroups?.params;

          // This attempts to find if a reserved parameter is used
          const isGaConnected = !!_(googleParams).find((trackingParam) => {
            return trackingParam.curalateReserved;
          });

          dispatch(receiveGAStateSucceeded(isGaConnected));
        },
      );

      dispatch(fetchGAEcommerceState());
    };
  }

  function fetchAggregateAndTrendStats(startDate: Date, endDate: Date) {
    return (dispatch) => {
      dispatch(requestPostSummaryStatsAction);

      const userTimezone = sessionVarsService.getTimeZoneId();
      const startDateString = moment(startDate)
        .tz(userTimezone)
        .format('YYYY-MM-DD');
      const endDateString = moment(endDate)
        .tz(userTimezone)
        .format('YYYY-MM-DD');

      PromotedPostsMetricsGatewayApi.getPromotedPostsMetricsSummary(
        startDateString,
        endDateString,
      ).then((response) => {
        const aggregateStats = response.aggregatePromotedPostMetrics;
        const trendStats = response.dailyPromotedPostSummary;

        dispatch(
          receivePostAggregateStats({
            [AggregatePromotedPostsValues.AmountSpent]: {
              label:
                AggregatePromotedPostsValueLabels[
                  AggregatePromotedPostsValues.AmountSpent
                ],
              value: aggregateStats.spend,
              tooltip:
                'Total spend across for all promoted posts within the selected time period.',
            },
            [AggregatePromotedPostsValues.TotalClicks]: {
              label:
                AggregatePromotedPostsValueLabels[
                  AggregatePromotedPostsValues.TotalClicks
                ],
              value: aggregateStats.clicks,
              tooltip:
                "Total number of clickthroughs to your promoted posts' links in the selected " +
                'time period.',
            },
            [AggregatePromotedPostsValues.AverageCPC]: {
              label:
                AggregatePromotedPostsValueLabels[
                  AggregatePromotedPostsValues.AverageCPC
                ],
              value: aggregateStats.cpc,
              isEmpty: angular.isUndefined(aggregateStats.cpc),
              tooltip:
                'Average Cost Per Click (CPC) is your total spend divided by your total clicks ' +
                'across all promoted posts within the selected time period.',
            },
            [AggregatePromotedPostsValues.TotalImpressions]: {
              label:
                AggregatePromotedPostsValueLabels[
                  AggregatePromotedPostsValues.TotalImpressions
                ],
              value: aggregateStats.impressions,
              tooltip:
                'Sum of views across all promoted posts within the selected time period.',
            },
            [AggregatePromotedPostsValues.TotalRevenue]: {
              label:
                AggregatePromotedPostsValueLabels[
                  AggregatePromotedPostsValues.TotalRevenue
                ],
              value: aggregateStats.revenue,
              isEmpty: angular.isUndefined(aggregateStats.revenue),
              tooltip:
                'Transaction revenue attributed to clickthroughs to site from all promoted ' +
                'posts in the selected time period using the attribution window defined by your analytics ' +
                'provider.',
            },
            [AggregatePromotedPostsValues.TotalROAS]: {
              label:
                AggregatePromotedPostsValueLabels[
                  AggregatePromotedPostsValues.TotalROAS
                ],
              value: aggregateStats.roas,
              isEmpty: angular.isUndefined(aggregateStats.roas),
              tooltip:
                'Total Return on Ad Spend (ROAS) is your total revenue divided by your total ' +
                'spend across all promoted posts within the selected time period.',
            },
          }),
        );

        const sparseTrendMetrics = _(trendStats).map(
          (
            day: IDailyPromotedPostSummary & { readableDate: Date | string },
          ) => {
            day.readableDate = day.date;
            // Fill in empty data for the day if missing
            day.clicks = day.clicks || 0;
            day.revenue = day.revenue || 0.0;
            day.spend = day.spend || 0.0;
            day.postCount = day.postCount || 0;

            return day;
          },
        );

        const trendMetrics = fillInMissingDates(
          sparseTrendMetrics,
          startDate,
          endDate,
          (date) => ({
            clicks: 0,
            revenue: 0,
            spend: 0,
            postCount: 0,
            date: new Date(date),
            readableDate: moment(date)
              .tz('America/New_York')
              .format('YYYY-MM-DD'),
          }),
        );

        dispatch(receivePostTrendStats(trendMetrics));
      });
    };
  }

  function changeDateRangeAndFetchPosts(dateRange: IUnisonDateRange) {
    const { startDate, endDate } = dateRange;
    return (dispatch, getState) => {
      // Change date range and refresh pagination
      dispatch(changeDateRange(dateRange));

      const { promotedPostsMetrics } = getState();
      dispatch(fetchAggregateAndTrendStats(startDate, endDate));
      dispatch(
        fetchPromotedPosts(
          startDate,
          endDate,
          promotedPostsMetrics.promotedPostsStatsLimit,
          promotedPostsMetrics.promotedPostsStatsOffset,
          promotedPostsMetrics.promotedPostsStatsSortKey,
          promotedPostsMetrics.promotedPostsStatsSortDir,
        ),
      );
    };
  }

  function changeTableParamsAndFetchPosts(
    dateRange: IUnisonDateRange,
    limit: number,
    offset: number,
    sortKey,
    sortDir,
  ) {
    return (dispatch) => {
      dispatch(
        fetchPromotedPosts(
          dateRange.startDate,
          dateRange.endDate,
          limit,
          offset,
          sortKey,
          sortDir,
        ),
      );
      dispatch(changePromotedPostsTableParams(limit, offset, sortKey, sortDir));
    };
  }

  function onDeletePromotion(campaignId, adAccountId, postId) {
    trackingService.recordEvent(
      trackingService.CATEGORY_PROMOTED_POSTS,
      trackingService.ACTION_DELETE_PROMOTION,
    );

    return (dispatch) => {
      return PromotedPostsMetricsGatewayApi.deletePromotion(
        campaignId,
        adAccountId,
      ).then(() => {
        dispatch(
          updateInsightsForPost(postId, PromotedPostsMetricsStatuses.DELETED),
        );
        return dispatch(deletePromotionAction(campaignId));
      });
    };
  }

  function onPausePromotion(campaignId, adAccountId, postId) {
    trackingService.recordEvent(
      trackingService.CATEGORY_PROMOTED_POSTS,
      trackingService.ACTION_PAUSE_PROMOTION,
    );

    return (dispatch) => {
      dispatch(pausePromotionAction(campaignId));
      return PromotedPostsMetricsGatewayApi.pausePromotion(
        campaignId,
        adAccountId,
      ).then((response) => {
        dispatch(
          updateInsightsForPost(postId, PromotedPostsMetricsStatuses.PAUSED),
        );
        return dispatch(updatePromotionStatusAction(campaignId, response));
      });
    };
  }

  function onResumePromotion(campaignId, adAccountId, postId) {
    trackingService.recordEvent(
      trackingService.CATEGORY_PROMOTED_POSTS,
      trackingService.ACTION_RESUME_PROMOTION,
    );

    return (dispatch) => {
      dispatch(resumePromotionAction(campaignId));
      return PromotedPostsMetricsGatewayApi.resumePromotion(
        campaignId,
        adAccountId,
      ).then((response) => {
        dispatch(
          updateInsightsForPost(postId, PromotedPostsMetricsStatuses.RUNNING),
        );
        return dispatch(updatePromotionStatusAction(campaignId, response));
      });
    };
  }

  function updateInsightsForPost(postId: string, desiredStatus: string) {
    trackingService.recordEvent(
      trackingService.CATEGORY_PROMOTED_POSTS,
      trackingService.ACTION_UPDATE_INSIGHTS_FOR_POST,
    );

    return (dispatch, getState) => {
      const { promotedPostsMetrics } = getState();
      dispatch(updateInsightsForPostAction(postId));
      return PromotedPostsMetricsGatewayApi.updateAdInsightsForPostId(
        postId,
        desiredStatus,
      ).then((response) => {
        const runningPromotions = promotedPostsMetrics.runningPromotionPostIds;
        if (
          response.status === PromotedPostsMetricsStatuses.RUNNING ||
          response.status === PromotedPostsMetricsStatuses.PENDING ||
          response.status === PromotedPostsMetricsStatuses.PAUSED
        ) {
          runningPromotions.add(response.postId);
        } else if (response.status === PromotedPostsMetricsStatuses.DELETED) {
          runningPromotions.delete(response.postId);
        }
        return dispatch(updateRunningPromotionPostIds(runningPromotions));
      });
    };
  }

  function updatePromotionStates() {
    return (dispatch, getState) => {
      const { promotedPostsMetrics, instagram } = getState();
      const promotionStates = {};
      const engagementPosts = instagram.engagement.posts.posts || [];
      const recentPosts = instagram.engagement.posts.recentPosts || [];
      const currentPosts = engagementPosts.concat(recentPosts);
      const runningPromotions = promotedPostsMetrics.runningPromotionPostIds;

      _.each(currentPosts, (post: any) => {
        promotionStates[
          post.cuid
        ] = PromotedPostsEligibility.getPromotionEligibilityStatus(
          post,
          runningPromotions,
        );
      });

      dispatch(updatePostPromotionStates(promotionStates));
    };
  }

  function requestCurrentlyRunningAds() {
    return (dispatch) => {
      dispatch(requestCurrentlyRunningAdsAction());
      PromotedPostsMetricsGatewayApi.getCurrentlyRunningAds().then(
        (response) => {
          const postIdsSet = new Set<string>();
          const posts = response?.posts || [];

          _.each(posts, (post) => {
            postIdsSet.add(post?.postData?.postId);
          });

          dispatch(updateRunningPromotionPostIds(postIdsSet));
          return dispatch(updatePromotionStates());
        },
      );
    };
  }

  return {
    fetchRecentPromotedPosts,
    fetchOnboardingStepsState,
    fetchOnboardingStepsStateAction,
    fetchGAState,
    changeDateRangeAndFetchPosts,
    fetchAggregateAndTrendStats,
    changeTrendsSelectedValue,
    changeTableParamsAndFetchPosts,
    onDeletePromotion,
    onPausePromotion,
    onResumePromotion,
    updateInsightsForPost,
    requestCurrentlyRunningAds,
    updatePromotionStates,
    receiveOnboardingStepsStateSucceeded,
    receiveOnboardingStepsStateFailed,
  };
}

promotedPostsMetricsModule.service('PromotedPostsMetricsActions', [
  '$window',
  '$state',
  'MetricsGatewayApiService',
  'PromotedPostsMetricsGatewayApiService',
  'PromotedPostsEligibilityService',
  'SessionVarsService',
  'TrackingService',
  'UniversalImageActionsApiService',
  PromotedPostsMetricsActions,
]);
